import type { SortOrder } from "../types/common";

export const sortByValue = <T extends Record<string, unknown>>(
	items: T[],
	primaryKey: keyof T,
	primaryOrder: SortOrder = "desc",
	secondaryKey?: keyof T,
	secondaryOrder: SortOrder = "asc",
): T[] => {
	if (!items.length) return items;

	return items.sort((a, b) => {
		const primaryComparison = compareValues(a[primaryKey], b[primaryKey], primaryOrder);

		if (primaryComparison !== 0) {
			return primaryComparison;
		} else if (secondaryKey) {
			return compareValues(a[secondaryKey], b[secondaryKey], secondaryOrder);
		} else {
			return 0;
		}
	});
};

const compareValues = (valueA: unknown, valueB: unknown, order: SortOrder): number => {
	let comparison = 0;

	if (typeof valueA === "number" && typeof valueB === "number") {
		comparison = valueA - valueB;
	} else if (typeof valueA === "string" && typeof valueB === "string") {
		comparison = valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: "base" });
	} else if (valueA != null && valueB != null) {
		comparison = valueA > valueB ? 1 : -1;
	}

	if (order === "desc") {
		comparison *= -1;
	}

	return comparison;
};

export const sortByOrder = <T extends Record<string, unknown>, K extends keyof T>(
	items: T[],
	key: K,
	order: string[] = [],
): T[] => {
	if (!items.length) return items;

	return items.sort((a, b) => {
		if (order.length > 0) {
			const lowerCaseOrder = order.map((o) => o.toLowerCase());
			const aValue = a[key];
			const bValue = b[key];
			const indexA = typeof aValue === "string" ? lowerCaseOrder.indexOf(aValue.toLowerCase()) : -1;
			const indexB = typeof bValue === "string" ? lowerCaseOrder.indexOf(bValue.toLowerCase()) : -1;

			if (indexA === -1 && indexB === -1) return 0;
			if (indexA === -1) return 1;
			if (indexB === -1) return -1;

			return indexA - indexB;
		}
		return 0;
	});
};
